import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaUserPlus } from 'react-icons/fa';
import styles from '../ClientManagement.module.css';
import { contactService, ContactHelper } from '../../services/contactService';
import { useAccessToken } from '../../hooks/useAuth';
import { useAlert } from '../../context/AlertContext';
import EditContactModal from '../../components/ui/EditContactModal';

/**
 * Component for displaying contact status badge
 */
const ContactStatusBadge = ({ isActive }) => {
    const isActiveBoolean = (() => {
        if (isActive === undefined || isActive === null) return false;
        if (typeof isActive === 'boolean') return isActive;
        if (typeof isActive === 'number') return isActive === 1;
        if (typeof isActive === 'string') return isActive.toLowerCase() === 'true' || isActive === '1';
        return false;
    })();
    
    return (
        <span className={`${styles.status} ${isActiveBoolean ? styles.active : styles.inactive}`}>
            {isActiveBoolean ? 'Active' : 'Inactive'}
        </span>
    );
};

/**
 * Component for contact action buttons
 */
const ContactActions = ({ contact, onEdit, loading }) => {
    return (
        <div className={styles.clientActions}>
            <button
                className={styles.actionButtonIcon}
                onClick={() => onEdit(contact)}
                disabled={loading}
                title="Edit contact"
            >
                <FaEdit />
            </button>
        </div>
    );
};

/**
 * ContactList Component - Displays all contacts with filtering and CRUD operations
 */
const ContactList = () => {
    const navigate = useNavigate();
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();

    // State management
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('active'); // 'all', 'active', 'inactive'
    const [contactStats, setContactStats] = useState({ total: 0, active: 0, inactive: 0 });

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState(null);
    const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'create'

    /**
     * Load all contacts from API using contactService
     */
    const loadContacts = useCallback(async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Load all contacts
            const allContacts = await contactService.getAllContacts(accessToken);
            
            setContacts(allContacts);
            
            // Calculate statistics
            const total = allContacts.length;
            const active = allContacts.filter(c => {
                const isActive = c.isActive;
                if (typeof isActive === 'boolean') return isActive;
                if (typeof isActive === 'number') return isActive === 1;
                if (typeof isActive === 'string') return isActive.toLowerCase() === 'true' || isActive === '1';
                return false;
            }).length;
            const inactive = total - active;
            
            setContactStats({ total, active, inactive });
        } catch (error) {
            console.error('Failed to load contacts:', error);
            setErrorAlert('Failed to load contacts');
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    /**
     * Apply filters to contacts
     */
    const applyFilters = useCallback(() => {
        let filtered = [...contacts];

        // Apply active filter
        if (activeFilter !== 'all') {
            filtered = filtered.filter(contact => {
                const isActive = contact.isActive;
                const isActiveBoolean = (() => {
                    if (typeof isActive === 'boolean') return isActive;
                    if (typeof isActive === 'number') return isActive === 1;
                    if (typeof isActive === 'string') return isActive.toLowerCase() === 'true' || isActive === '1';
                    return false;
                })();
                
                return activeFilter === 'active' ? isActiveBoolean : !isActiveBoolean;
            });
        }

        // Apply search filter
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(contact => {
                const firstName = (contact.firstName || '').toLowerCase();
                const lastName = (contact.lastName || '').toLowerCase();
                const email = (contact.email || '').toLowerCase();
                const fullName = `${firstName} ${lastName}`.trim();
                
                return fullName.includes(search) || 
                       firstName.includes(search) || 
                       lastName.includes(search) ||
                       email.includes(search);
            });
        }

        setFilteredContacts(filtered);
    }, [contacts, activeFilter, searchTerm]);

    /**
     * Handle edit button click
     */
    const handleEdit = (contact) => {
        setEditingContact(contact);
        setModalMode('edit');
        setEditModalOpen(true);
    };

    /**
     * Handle add new contact button click
     */
    const handleAddContact = () => {
        setEditingContact(null);
        setModalMode('create');
        setEditModalOpen(true);
    };

    /**
     * Handle contact updated from edit modal
     */
    const handleContactUpdated = async (updatedContact, wasDeleted = false) => {
        if (wasDeleted) {
            // Contact was deleted, reload the contacts list
            await loadContacts();
        } else if (updatedContact) {
            // Contact was updated, reload the contacts list to get fresh data
            await loadContacts();
        }
        // Close the modal
        setEditModalOpen(false);
        setEditingContact(null);
    };

    /**
     * Handle close edit modal
     */
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setEditingContact(null);
    };

    // Load contacts on mount
    useEffect(() => {
        loadContacts();
    }, [loadContacts]);

    // Apply filters when dependencies change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    return (
        <div className={styles.clientListSection}>
            <div className={styles.resultsHeader}>
                <h2>All Contacts ({contactStats.total})</h2>
                <button
                    className={styles.addButton}
                    onClick={handleAddContact}
                >
                    <FaUserPlus /> Add New Contact
                </button>
            </div>

            {/* Statistics Bar */}
            <div className={styles.statsGrid} style={{ marginBottom: '2rem' }}>
                <div className={styles.statCard}>
                    <h3>Total Contacts</h3>
                    <div className={styles.statNumber}>{contactStats.total}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Active</h3>
                    <div className={styles.statNumber}>{contactStats.active}</div>
                </div>
                <div className={styles.statCard}>
                    <h3>Inactive</h3>
                    <div className={styles.statNumber}>{contactStats.inactive}</div>
                </div>
            </div>

            {/* Filter Controls */}
            <div className={styles.searchForm}>
                <div className={styles.searchInputGroup}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search contacts by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className={styles.filterButtons}>
                    <button
                        className={`${styles.filterButton} ${activeFilter === 'all' ? styles.active : ''}`}
                        onClick={() => setActiveFilter('all')}
                    >
                        All Contacts
                    </button>
                    <button
                        className={`${styles.filterButton} ${activeFilter === 'active' ? styles.active : ''}`}
                        onClick={() => setActiveFilter('active')}
                    >
                        Active Only
                    </button>
                    <button
                        className={`${styles.filterButton} ${activeFilter === 'inactive' ? styles.active : ''}`}
                        onClick={() => setActiveFilter('inactive')}
                    >
                        Inactive Only
                    </button>
                </div>
            </div>

            {/* Results Section */}
            {loading ? (
                <div className={styles.loadingMessage}>
                    <div className={styles.spinner}></div>
                    Loading contacts...
                </div>
            ) : filteredContacts.length === 0 ? (
                <div className={styles.noResults}>
                    {searchTerm ? 'No contacts found matching your search criteria.' : 'No contacts found.'}
                </div>
            ) : (
                <div className={styles.clientGrid}>
                    {filteredContacts.map((contact) => (
                        <div key={contact.contactId} className={styles.clientCard}>
                            <div className={styles.clientHeader}>
                                <div className={styles.clientTitleGroup}>
                                    <h3>
                                        {ContactHelper.getFullName(contact)}
                                    </h3>
                                </div>
                                <ContactStatusBadge isActive={contact.isActive} />
                            </div>

                            <div className={styles.clientDetails}>
                                {contact.email && (
                                    <p><strong>Email:</strong> {contact.email}</p>
                                )}
                                {contact.phone && (
                                    <p><strong>Phone:</strong> {contact.phone}</p>
                                )}
                            </div>

                            <div className={styles.clientTypeTags}>
                                {/* Contact types will be displayed here when available */}
                            </div>

                            <ContactActions
                                contact={contact}
                                onEdit={handleEdit}
                                loading={loading}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Edit/Add Contact Modal */}
            <EditContactModal
                contact={editingContact}
                mode={modalMode}
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                onContactUpdated={handleContactUpdated}
            />
        </div>
    );
};

export default ContactList;
